// Página AdminPurchaseOrders - Gestión de pedidos de compra
// Listado de pedidos con filtros, búsqueda y estadísticas

import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { usePurchaseOrders } from '../../hooks/usePurchaseOrders'
import { formatCurrency, formatDate } from '../../utils/helpers'
import Button from '../../components/Button'
import PurchaseOrderStatusBadge from '../../components/purchaseOrders/PurchaseOrderStatusBadge'
import PaymentProgressBar from '../../components/purchaseOrders/PaymentProgressBar'
import ReceiptProgressBar from '../../components/purchaseOrders/ReceiptProgressBar'
import Toast from '../../components/Toast'
import './AdminPurchaseOrders.css'

function AdminPurchaseOrders() {
  const [statusFilter, setStatusFilter] = useState('all')
  const [supplierFilter, setSupplierFilter] = useState('all')
  const [searchTerm, setSearchTerm] = useState('')
  const [sortBy, setSortBy] = useState('createdAt')
  const [sortDirection, setSortDirection] = useState('desc')
  const [notification, setNotification] = useState(null)

  const { orders, loading, error, refetch } = usePurchaseOrders({})
  const [filteredOrders, setFilteredOrders] = useState([])
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    confirmed: 0,
    paid: 0,
    received: 0,
    completed: 0,
    cancelled: 0
  })

  // Aplicar filtros
  useEffect(() => {
    if (!orders) return

    let filtered = [...orders]

    // Filtrar por estado
    if (statusFilter !== 'all') {
      filtered = filtered.filter(order => order.status === statusFilter)
    }

    // Filtrar por proveedor
    if (supplierFilter !== 'all') {
      filtered = filtered.filter(order => 
        order.suppliers?.some(s => s.supplierId === supplierFilter)
      )
    }

    // Búsqueda
    if (searchTerm) {
      const term = searchTerm.toLowerCase()
      filtered = filtered.filter(order => 
        order.orderNumber?.toLowerCase().includes(term) ||
        order.suppliers?.some(s => 
          s.supplierName?.toLowerCase().includes(term) ||
          s.items?.some(item => item.name?.toLowerCase().includes(term))
        )
      )
    }

    // Ordenar
    filtered.sort((a, b) => {
      let aVal = a[sortBy]
      let bVal = b[sortBy]

      if (sortBy === 'createdAt' || sortBy === 'expectedDeliveryDate') {
        aVal = aVal?.toDate ? aVal.toDate() : new Date(aVal)
        bVal = bVal?.toDate ? bVal.toDate() : new Date(bVal)
      }

      if (sortDirection === 'asc') {
        return aVal > bVal ? 1 : -1
      } else {
        return aVal < bVal ? 1 : -1
      }
    })

    setFilteredOrders(filtered)
  }, [orders, statusFilter, supplierFilter, searchTerm, sortBy, sortDirection])

  // Calcular estadísticas
  useEffect(() => {
    if (!orders) return

    const statsData = {
      total: orders.length,
      pending: 0,
      confirmed: 0,
      paid: 0,
      received: 0,
      completed: 0,
      cancelled: 0
    }

    orders.forEach(order => {
      const status = order.status || 'pending'
      if (status === 'pending') statsData.pending++
      else if (status === 'confirmed' || status === 'partially_paid') statsData.confirmed++
      else if (status === 'paid') statsData.paid++
      else if (status === 'received' || status === 'partially_received') statsData.received++
      else if (status === 'completed') statsData.completed++
      else if (status === 'cancelled') statsData.cancelled++
    })

    setStats(statsData)
  }, [orders])

  // Obtener lista única de proveedores
  const suppliers = orders ? Array.from(new Set(
    orders.flatMap(order => 
      order.suppliers?.map(s => ({ id: s.supplierId, name: s.supplierName })) || []
    )
  )).filter((v, i, a) => a.findIndex(t => t.id === v.id) === i) : []

  const exportToCSV = () => {
    const headers = ['Número', 'Fecha', 'Proveedores', 'Items', 'Total', 'Pagado', 'Recibido', 'Estado']
    const rows = filteredOrders.map(order => {
      const date = order.createdAt?.toDate ? formatDate(order.createdAt.toDate()) : formatDate(new Date())
      const suppliersList = order.suppliers?.map(s => s.supplierName).join('; ') || ''
      
      return [
        order.orderNumber || '',
        date,
        suppliersList,
        order.totalItems || 0,
        order.total || 0,
        order.totalPaid || 0,
        order.totalReceived || 0,
        order.status || ''
      ]
    })

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n')

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)
    link.setAttribute('href', url)
    link.setAttribute('download', `pedidos_compra_${new Date().toISOString().split('T')[0]}.csv`)
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)

    setNotification({
      type: 'success',
      message: `Se exportaron ${filteredOrders.length} pedidos a CSV`
    })
    setTimeout(() => setNotification(null), 3000)
  }

  if (loading) {
    return (
      <div className="admin-purchase-orders-page">
        <div className="loading-state">
          <p>Cargando pedidos de compra...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="admin-purchase-orders-page">
        <div className="error-state">
          <p>{error}</p>
          <Button onClick={refetch}>Reintentar</Button>
        </div>
      </div>
    )
  }

  return (
    <div className="admin-purchase-orders-page">
      <Toast
        isVisible={!!notification}
        message={notification?.message || ''}
        type={notification?.type || 'error'}
        onClose={() => setNotification(null)}
      />

      <div className="orders-header">
        <div>
          <h1>Gestión de Pedidos de Compra</h1>
          <p className="orders-subtitle">
            Administra y gestiona todos los pedidos a proveedores
          </p>
        </div>
        <div className="header-actions">
          <Button onClick={exportToCSV} variant="outline">
            📥 Exportar CSV
          </Button>
          <Link to="/admin/pedidos-compra/nuevo">
            <Button variant="primary">
              ➕ Nuevo Pedido
            </Button>
          </Link>
        </div>
      </div>

      {/* Estadísticas */}
      <div className="orders-stats">
        <div className="stat-card">
          <div className="stat-value">{stats.total}</div>
          <div className="stat-label">Total</div>
        </div>
        <div className="stat-card stat-pending">
          <div className="stat-value">{stats.pending}</div>
          <div className="stat-label">Pendientes</div>
        </div>
        <div className="stat-card stat-confirmed">
          <div className="stat-value">{stats.confirmed}</div>
          <div className="stat-label">Confirmados</div>
        </div>
        <div className="stat-card stat-paid">
          <div className="stat-value">{stats.paid}</div>
          <div className="stat-label">Pagados</div>
        </div>
        <div className="stat-card stat-received">
          <div className="stat-value">{stats.received}</div>
          <div className="stat-label">Recibidos</div>
        </div>
        <div className="stat-card stat-completed">
          <div className="stat-value">{stats.completed}</div>
          <div className="stat-label">Completados</div>
        </div>
      </div>

      {/* Filtros */}
      <div className="orders-filters">
        <div className="filter-group">
          <label htmlFor="statusFilter">Estado:</label>
          <select
            id="statusFilter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">Todos</option>
            <option value="pending">Pendiente</option>
            <option value="confirmed">Confirmado</option>
            <option value="partially_paid">Pago Parcial</option>
            <option value="paid">Pagado</option>
            <option value="partially_received">Recepción Parcial</option>
            <option value="received">Recibido</option>
            <option value="completed">Completado</option>
            <option value="cancelled">Cancelado</option>
          </select>
        </div>

        <div className="filter-group">
          <label htmlFor="supplierFilter">Proveedor:</label>
          <select
            id="supplierFilter"
            value={supplierFilter}
            onChange={(e) => setSupplierFilter(e.target.value)}
          >
            <option value="all">Todos</option>
            {suppliers.map(supplier => (
              <option key={supplier.id} value={supplier.id}>
                {supplier.name}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label htmlFor="searchTerm">Buscar:</label>
          <input
            type="text"
            id="searchTerm"
            placeholder="Número, proveedor, producto..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="filter-group">
          <label htmlFor="sortBy">Ordenar por:</label>
          <select
            id="sortBy"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
          >
            <option value="createdAt">Fecha</option>
            <option value="orderNumber">Número</option>
            <option value="total">Total</option>
            <option value="status">Estado</option>
          </select>
        </div>

        <div className="filter-group">
          <label htmlFor="sortDirection">Dirección:</label>
          <select
            id="sortDirection"
            value={sortDirection}
            onChange={(e) => setSortDirection(e.target.value)}
          >
            <option value="desc">Descendente</option>
            <option value="asc">Ascendente</option>
          </select>
        </div>
      </div>

      {/* Tabla */}
      <div className="orders-table-container">
        {filteredOrders.length === 0 ? (
          <div className="empty-state">
            <p>No se encontraron pedidos con los filtros aplicados</p>
            {!searchTerm && statusFilter === 'all' && supplierFilter === 'all' && (
              <Link to="/admin/pedidos-compra/nuevo">
                <Button variant="primary" style={{ marginTop: '1rem' }}>
                  ➕ Crear Primer Pedido
                </Button>
              </Link>
            )}
          </div>
        ) : (
          <table className="orders-table">
            <thead>
              <tr>
                <th>Número</th>
                <th>Fecha</th>
                <th>Proveedores</th>
                <th>Items</th>
                <th>Total</th>
                <th>% Pagado</th>
                <th>% Recibido</th>
                <th>Estado</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filteredOrders.map((order) => {
                const date = order.createdAt?.toDate ? formatDate(order.createdAt.toDate()) : formatDate(new Date())
                const suppliersList = order.suppliers?.slice(0, 2).map(s => s.supplierName).join(', ') || ''
                const moreSuppliers = order.suppliers?.length > 2 ? ` +${order.suppliers.length - 2} más` : ''
                // Calcular cantidades recibidas (no montos)
                const totalItemsReceived = order.suppliers?.reduce((sum, supplier) => {
                  return sum + (supplier.items?.reduce((itemSum, item) => {
                    const received = (item.receivedQuantity || 0) - (item.defectiveQuantity || 0)
                    return itemSum + Math.max(0, received)
                  }, 0) || 0)
                }, 0) || 0
                
                const totalItemsOrdered = order.totalItems || 0

                return (
                  <tr key={order.id}>
                    <td>
                      <Link to={`/admin/pedidos-compra/${order.id}`} className="order-number-link">
                        {order.orderNumber || order.id}
                      </Link>
                    </td>
                    <td>{date}</td>
                    <td>
                      <div className="suppliers-cell">
                        {suppliersList}
                        {moreSuppliers && <span className="more-suppliers">{moreSuppliers}</span>}
                      </div>
                    </td>
                    <td>{totalItemsOrdered}</td>
                    <td>{formatCurrency(order.total || 0)}</td>
                    <td>
                      <PaymentProgressBar
                        paidAmount={order.totalPaid || 0}
                        totalAmount={order.total || 0}
                        showLabel={false}
                      />
                    </td>
                    <td>
                      <ReceiptProgressBar
                        receivedQuantity={totalItemsReceived}
                        totalQuantity={totalItemsOrdered}
                        showLabel={false}
                      />
                    </td>
                    <td>
                      <PurchaseOrderStatusBadge status={order.status} />
                    </td>
                    <td>
                      <Link to={`/admin/pedidos-compra/${order.id}`}>
                        <Button variant="outline" size="small">
                          Ver
                        </Button>
                      </Link>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}

export default AdminPurchaseOrders

